import React from "react";
import { useHistory, useParams } from "react-router-dom";

import { Context } from "../../../studentStore";
import { IAnswerR, IStudent, IIssueR, IOpenClassList } from "../../../state";
import { groupPath } from "../../../studentStore";
import { parseQueryParams } from "../../../libs/Util";
import AnswerBox from "../../../molecules/AnswerBox";
import EveryoneAnswerModalClassDropdown from "../../../molecules/EveryoneAnswerModalClassDropdown";

import styles from "./AnswerList.module.scss";

export type AnswerListProps = {
  issue: IIssueR;
  answers: IAnswerR[];
  students: IStudent[];
  currentUser: IStudent;
  calling: boolean;
  handleComment: (answerId: number, text: string, commentId?: number) => void;
  handleDeleteReply: (answerId: number, commentId: number) => void;
  handleLike: (answerId: number) => void;
  klasses: IOpenClassList[] | [];
  setSelectedValues: React.Dispatch<React.SetStateAction<number[]>>;
  selectedValues: number[] | [];
  cram_school: boolean;
};

enum ETab {
  all = "クラスの回答",
  byGroup = "グループの回答",
}

const AnswerList: React.FC<AnswerListProps> = ({
  issue,
  answers,
  handleComment,
  handleDeleteReply,
  students,
  currentUser,
  handleLike,
  calling,
  klasses,
  selectedValues,
  setSelectedValues,
  cram_school,
}) => {
  const { state } = React.useContext(Context);
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const lessonParam = parseInt(parseQueryParams()["lesson"]);
  const pageParam = parseInt(parseQueryParams()["page"]);
  const [tabActive, setTabActive] = React.useState<ETab>(ETab.all);

  const currentStudent = React.useMemo(() => {
    return students.find(student => student.id === currentUser.id);
  }, [students, currentUser]);

  const myAnswer = React.useMemo(() => {
    return answers.filter(answer => answer.student.id === currentUser.id);
  }, [answers, currentUser]);

  const answeredStudents = answers.filter(
    answer => answer.student.group?.id === currentStudent?.group_id,
  );

  const myGroup = state.groups.find(
    group => group.id === currentStudent?.group_id,
  );

  const groupStudentTotal = students.filter(
    student => student.group_id === currentStudent?.group_id,
  ).length;

  const renderGroupHeader = React.useCallback(() => {
    return tabActive === ETab.byGroup && !!myGroup ? (
      <div className={styles.controller}>
        <div className={styles.head}>
          <span className={styles.class}>
            <span className={styles.label}>参加しているグループ</span>
            <span className={styles.groupName}>{myGroup?.name ?? ""}</span>
          </span>
          <span className={styles.answerCount}>
            {answeredStudents.length}/{groupStudentTotal}人回答
          </span>
        </div>
      </div>
    ) : null;
  }, [tabActive, answeredStudents, groupStudentTotal, myGroup]);

  const renderAnswer = React.useCallback(
    (students: IStudent[], answers: IAnswerR[]) => {
      const { name_opened, answer_opened } = issue || {
        name_opened: false,
        answer_opened: false,
      };

      if (tabActive === ETab.byGroup && !myGroup) {
        return (
          <div className={styles.group}>
            <p className={styles.emptyMessage}>
              まだグループに参加していません。
            </p>
            <div className={styles.buttonGroup}>
              <button
                className={styles.groupCreateButton}
                onClick={() => {
                  history.push({
                    pathname: groupPath,
                    state: {
                      materialId: id,
                      lessonId: lessonParam,
                      page: pageParam,
                    },
                  });
                }}
              >
                グループに参加する
              </button>
            </div>
            <p className={styles.emptyMessageBox}>まだ回答がありません。</p>
          </div>
        );
      }

      if (name_opened) {
        if (!answer_opened) {
          return (
            <>
              {renderGroupHeader()}
              <AnswerBox
                student={answers.length ? answers[0].student : currentUser}
                name_opened={true}
                answers={answers}
                currentUser={currentUser}
                calling={calling}
                onSendComment={handleComment}
                onDeleteReply={handleDeleteReply}
                handleLike={handleLike}
              />
            </>
          );
        }

        return (
          <div className={styles.answerList}>
            {renderGroupHeader()}
            {cram_school && tabActive !== ETab.byGroup && (
              <div className={styles.classDropdownContainer}>
                <EveryoneAnswerModalClassDropdown
                  klasses={klasses}
                  selectedValues={selectedValues}
                  setSelectedValues={setSelectedValues}
                />
              </div>
            )}
            <AnswerBox
              student={currentStudent}
              name_opened={true}
              answers={myAnswer}
              currentUser={currentUser}
              calling={calling}
              isMyAnswer
              onSendComment={handleComment}
              onDeleteReply={handleDeleteReply}
              handleLike={handleLike}
            />
            {students
              .filter(student => student.id !== currentUser.id)
              .map(student => (
                <AnswerBox
                  key={student.id}
                  student={student}
                  name_opened={true}
                  answers={answers.filter(
                    answer => answer.student.id === student.id,
                  )}
                  currentUser={currentUser}
                  calling={calling}
                  onSendComment={handleComment}
                  onDeleteReply={handleDeleteReply}
                  handleLike={handleLike}
                />
              ))}
          </div>
        );
      }

      return (
        <>
          {renderGroupHeader()}
          {cram_school && tabActive !== ETab.byGroup && (
            <div className={styles.classDropdownContainer}>
              <EveryoneAnswerModalClassDropdown
                klasses={klasses}
                selectedValues={selectedValues}
                setSelectedValues={setSelectedValues}
              />
            </div>
          )}
          <AnswerBox
            name_opened={false}
            answer_opened={answer_opened}
            answers={answers}
            currentUser={currentUser}
            calling={calling}
            onSendComment={handleComment}
            onDeleteReply={handleDeleteReply}
            handleLike={handleLike}
          />
        </>
      );
    },
    [
      calling,
      currentUser,
      issue,
      currentStudent,
      myAnswer,
      handleComment,
      handleDeleteReply,
      handleLike,
      klasses,
      selectedValues,
      setSelectedValues,
      cram_school,
      tabActive,
      renderGroupHeader,
      id,
      history,
      lessonParam,
      myGroup,
      pageParam,
    ],
  );

  const renderList = React.useCallback(() => {
    const isGroupTab = tabActive === ETab.byGroup;
    let listStudents: IStudent[] = [];
    let listAnswers: IAnswerR[] = [];
    if (isGroupTab) {
      listStudents = [
        ...students.filter(
          student =>
            student.group_id === currentUser.group_id &&
            currentUser.group_id !== 0,
        ),
      ];
      listAnswers = [
        ...answers.filter(
          answer =>
            !!answer.student.group?.id &&
            answer.student.group.id === currentUser.group_id,
        ),
      ];
    } else {
      listStudents = [...students];
      listAnswers = [...answers];
    }

    const answerTimeMap = new Map<number, number>();

    listAnswers.forEach(answer => {
      const studentId = answer.student.id;
      const time = new Date(answer.updated_at).getTime();
      answerTimeMap.set(studentId, time);
    });

    const sortedStudentsByAnswerTimeDesc = [...listStudents].sort((a, b) => {
      const aTime = answerTimeMap.get(a.id) ?? -Infinity;
      const bTime = answerTimeMap.get(b.id) ?? -Infinity;

      if (bTime !== aTime) {
        return bTime - aTime;
      }

      return a.id - b.id;
    });

    return renderAnswer(sortedStudentsByAnswerTimeDesc, listAnswers);
  }, [answers, currentUser.group_id, students, tabActive, renderAnswer]);

  return (
    <div className={styles.wrapperGroup}>
      <ul className={styles.tab}>
        {(Object.keys(ETab) as Array<keyof typeof ETab>).map(tab => (
          <li
            key={tab}
            onClick={() => setTabActive(ETab[tab])}
            className={tabActive === ETab[tab] ? styles.active : ""}
          >
            {ETab[tab]}
          </li>
        ))}
      </ul>
      {renderList()}
    </div>
  );
};

export default AnswerList;
